import Option, { TOptionRef } from '@atlas/uicorn/lib/Control/Option';
import Text from '@atlas/uicorn/lib/Display/Text';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import PriorityIcon from '@atlas/uicorn/lib/Icon/PriorityIcon';
import type { TThemeColor } from '@atlas/uicorn/lib/System/StylesProvider';
import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled from 'styled-components';
import useRefCallback from 'use-ref-callback';

const Label = React.memo(
    React.forwardRef(function Label(props: TLabelProps, ref: React.ForwardedRef<HTMLDivElement>): React.ReactElement {
        const { children, color, outlineColor, onRemove, prefix, removeText = 'Remove', ...elementProps } = props;

        const handleRemove = useRefCallback<React.MouseEventHandler<TOptionRef>>((e) => {
            if (!onRemove) return;
            e.preventDefault();
            onRemove(children);
        });

        return (
            <SLabel
                ref={ref}
                $color={color}
                $outlineColor={outlineColor}
                $textOnly={!prefix && !onRemove}
                {...elementProps}
            >
                {prefix ? typeof prefix === 'string' ? <span>{prefix}</span> : prefix : null}
                {children ? <Text children={children} ellipsis /> : null}
                {onRemove && (
                    <SRemoveButton tooltip={removeText} onClick={handleRemove}>
                        <CrossIcon size={15} />
                    </SRemoveButton>
                )}
            </SLabel>
        );
    }),
);

export default Label;

// #region Styles

const SLabel = styled.div<{
    $color?: TLabelProps['color'];
    $outlineColor?: TLabelProps['outlineColor'];
    $textOnly: boolean;
}>`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    height: 2.2rem;
    max-width: 100%;
    overflow: hidden;

    padding: 0 ${(p) => (p.$textOnly ? '0.6rem' : '0.4rem')};
    border-radius: ${(p) => p.theme.radius.small};

    background: ${(p) =>
        p?.$color ? (p.$color in p.theme.color ? p.theme.color[p.$color as TThemeColor] : p.$color) : 'transparent'};

    color: ${(p) => {
        if (p?.$color && !p.$color.startsWith('#') && p.$color in backgroundToTextColorMap) {
            return p.theme.color[backgroundToTextColorMap[p.$color]] || p.theme.color.grey9;
        }

        const hex = p?.$color ? (p.$color in p.theme.color ? p.theme.color[p.$color as TThemeColor] : p.$color) : null;

        return hex ? (isDarkColor(hex) ? p.theme.color.grey00 : p.theme.color.grey90) : p.theme.color.grey9;
    }};
    ${(p) =>
        p?.$outlineColor
            ? `outline: 1px solid ${
                  p.$outlineColor in p.theme.color ? p.theme.color[p.$outlineColor as TThemeColor] : p.$outlineColor
              };`
            : null}

    font: ${(p) => p.theme.font.normal};
    white-space: nowrap;
`;

const backgroundToTextColorMap: Record<string, TThemeColor> = {
    grey0: 'grey9',
    grey5: 'grey9',
    grey6: 'grey9',
    grey7: 'grey00',
    grey8: 'grey0',
    grey9: 'grey0',
    green5: 'grey91',
    green6: 'grey90',
    green7: 'grey00',
    red0: 'grey9',
    red6: 'grey00',
    red7: 'grey00',
    yellow2: 'grey91',
    yellow4: 'grey91',
    yellow5: 'grey91',
    yellow6: 'grey00',
    blue5: 'grey9',
    blue7: 'grey0',
    blue8: 'grey0',
};

// TODO: move to tool/box & test + parse ensure the value is indeed a hex color
function isDarkColor(hex: string) {
    const rgb = [parseInt(hex[1] + hex[2], 16), parseInt(hex[3] + hex[4], 16), parseInt(hex[5] + hex[6], 16)];
    const darknessIndex = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114;
    return darknessIndex <= 186;
}

// TODO: could not use IconButton, as it increased the height by a pixel or two
const SRemoveButton = styled(Option).attrs({ noHoverStyle: true })`
    color: ${(p) => p.theme.color.grey8};
`;

// #endregion

// #region Types

export type TLabelProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'color' | 'prefix'> & {
    children: string;
    color?: TThemeColor | string;
    outlineColor?: TThemeColor | string;
    prefix?: React.ReactNode;
    ellipsis?: boolean;
    onRemove?: (labelText: string) => void;
    removeText?: string;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <div
                style={{
                    display: 'inherit',
                    gap: 'inherit',
                    flexWrap: 'wrap',
                    width: props.narrowContainer ? '60px' : 'auto',
                }}
            >
                <Label children={props.message} color="grey6" />
                <Label children={props.message} color="gradientPrimaryEnd" />
                <Label children={props.message} color="#654321" />
                <Label children={props.message} color="#000000" />
                <Label children={props.message} />
                <Label children={props.message} color="grey5" />
                <Label children={props.message} color="grey6" />
                <Label children={props.message} color="grey7" />
                <Label children={props.message} color="grey8" />
                <Label children={props.message} color="grey9" />
                <Label children={props.message} color="green5" />
                <Label children={props.message} color="green6" />
                <Label children={props.message} color="green7" />
                <Label children={props.message} color="red0" />
                <Label children={props.message} color="red6" />
                <Label children={props.message} color="red7" />
                <Label children={props.message} color="yellow2" />
                <Label children={props.message} color="yellow4" />
                <Label children={props.message} color="yellow5" />
                <Label children={props.message} color="yellow6" />
                <Label children={props.message} color="blue5" />
                <Label children={props.message} color="blue7" />
                <Label children={props.message} color="blue8" />
                <Label children={props.message} color="orange" onRemove={props.onRemove} />

                <Label prefix={<PriorityIcon size={15} level={0} />}>No priority</Label>
                <Label prefix={<PriorityIcon size={15} level={1} />}>Low</Label>
                <Label prefix={<PriorityIcon size={15} level={2} />}>Medium</Label>
                <Label prefix={<PriorityIcon size={15} level={3} />}>High</Label>
                <Label prefix={<PriorityIcon size={15} level={4} />}>Urgent</Label>
            </div>
        );
    },
    controls: {
        message: { type: 'string', initial: 'Hello, World!' },
        narrowContainer: 'boolean',
    },
    handlers: { onRemove: 'console' },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Label', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render message="Hello world!" />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
